import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TreePine, Bell, Plus, X, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED_HEX = "#9E2B25";
const RED = "text-[#9E2B25]";

const MENU_ITEMS = [
  "Baked Mac & Cheese",
  "Southern Cornbread Dressing",
  "Stuffing",
  "Candied Yams",
  "Mashed Potatoes",
  "Green Bean Casserole",
  "Sweet Potato Pie",
  "Apple Pie",
];

const SIZES = [
  { value: "half", label: "Half tray — $30", cents: 3000 },
  { value: "full", label: "Full tray — $45", cents: 4500 },
];

const money = (c) => `$${(c / 100).toFixed(2)}`;
const today = new Date().toISOString().slice(0, 10);

const RED_BUTTON =
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-[#9E2B25] px-9 py-3.5 text-sm font-medium tracking-wide text-[#F8F2EA] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60";

export default function HolidayOrderPage() {
  const [lines, setLines] = useState([{ item: MENU_ITEMS[0], size: "half", qty: 1 }]);
  const [fulfillment, setFulfillment] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", pickup_date: "", pickup_time: "", delivery_date: "", delivery_address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState(null);

  const subtotal = lines.reduce((sum, l) => sum + (SIZES.find((s) => s.value === l.size)?.cents || 0) * (Number(l.qty) || 0), 0);
  const valid = lines.every((l) => l.item && Number(l.qty) >= 1) && fulfillment && form.name && form.email && form.phone &&
    (fulfillment === "delivery" ? form.delivery_date && form.delivery_address : form.pickup_date);

  const setLine = (i, key, value) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));
  const addLine = () => setLines((ls) => [...ls, { item: MENU_ITEMS[0], size: "half", qty: 1 }]);
  const removeLine = (i) => setLines((ls) => (ls.length === 1 ? ls : ls.filter((_, idx) => idx !== i)));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !valid) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/holiday/order`, { ...form, items: lines, fulfillment });
      setOrderId(data.order_id);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (orderId) {
    return (
      <div className="bg-[#F8F2EA]">
        <section className="mx-auto max-w-3xl px-6 py-24 text-center md:py-32" data-testid="holiday-confirmation">
          <Bell size={22} style={{ color: RED_HEX }} aria-hidden="true" className="mx-auto" />
          <div className="mx-auto mt-6 h-px w-14 bg-[#E1AA54]" aria-hidden="true" />
          <h1 className={`mt-8 font-display text-4xl font-light md:text-5xl ${RED}`}>Thank you!</h1>
          <p className="mt-6 text-lg leading-relaxed text-ink/85">
            Your holiday order is in. Check your email for a summary — we'll be in touch to confirm the details and
            payment.
          </p>
          <Link to="/" data-testid="holiday-confirmation-home-link" className="link-underline mt-10 inline-flex items-center gap-2 text-sm font-medium text-terracotta">
            Back to Sita <ArrowRight size={15} />
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-[#F8F2EA]">
      <Seo title="Holiday Order — By the Tray · Sita" description="Order Sita's holiday trays — mac & cheese, cornbread dressing, candied yams, pies and more. Pickup or delivery in Jersey City." />

      {/* ---------- Heading ---------- */}
      <section className="mx-auto max-w-7xl px-6 pb-10 pt-14 md:px-10 md:pb-14 md:pt-20" data-testid="holiday-order-hero">
        <div className="max-w-3xl">
          <FadeUp>
            <p className={`flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] ${RED}`}>
              <TreePine size={15} aria-hidden="true" /> Thanksgiving &amp; Christmas
            </p>
            <h1 className={`mt-4 font-display text-5xl font-light md:text-6xl ${RED}`}>Order by the tray</h1>
            <div className="mt-5 h-px w-20 bg-[#E1AA54]" aria-hidden="true" />
            <p className="mt-6 text-lg leading-relaxed text-ink/85">
              Half tray $30 · Full tray $45. Pick your items, choose pickup or delivery, and we'll confirm by email.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* ---------- Menu list ---------- */}
          <FadeUp delay={0.05} className="lg:col-span-4">
            <div data-testid="holiday-menu-list" className="rounded-3xl border border-[#9E2B25]/15 bg-white/60 p-8">
              <h2 className={`font-display text-2xl ${RED}`}>This season's trays</h2>
              <ul className="mt-6 space-y-4">
                {MENU_ITEMS.map((item) => (
                  <li key={item} className="flex items-center justify-between gap-4 border-b border-[#E1AA54]/40 pb-4 text-sm last:border-0 last:pb-0">
                    <span className="text-forest">{item}</span>
                    <span className="shrink-0 text-ink/60">$30 / $45</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs leading-relaxed text-ink/60">
                Tell us about allergies and we will cook around them.
              </p>
            </div>
          </FadeUp>

          {/* ---------- Order form ---------- */}
          <FadeUp delay={0.15} className="lg:col-span-8">
            <form onSubmit={submit} data-testid="holiday-order-form" className="rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10">
              {/* Order builder */}
              <h2 className={`font-display text-2xl ${RED}`}>Your trays</h2>
              <div className="mt-6 space-y-4">
                {lines.map((line, i) => (
                  <div key={i} data-testid={`holiday-line-${i + 1}`} className="grid gap-3 rounded-2xl border border-forest/10 p-4 sm:grid-cols-12">
                    <div className="sm:col-span-5">
                      <label className="field-label" htmlFor={`hl-item-${i}`}>Item</label>
                      <select id={`hl-item-${i}`} value={line.item} onChange={(e) => setLine(i, "item", e.target.value)} data-testid={`holiday-line-item-${i + 1}`} className="field">
                        {MENU_ITEMS.map((item) => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-3">
                      <label className="field-label" htmlFor={`hl-size-${i}`}>Size</label>
                      <select id={`hl-size-${i}`} value={line.size} onChange={(e) => setLine(i, "size", e.target.value)} data-testid={`holiday-line-size-${i + 1}`} className="field">
                        {SIZES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor={`hl-qty-${i}`}>Qty</label>
                      <input id={`hl-qty-${i}`} type="number" min="1" max="50" value={line.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`holiday-line-qty-${i + 1}`} className="field" />
                    </div>
                    <div className="flex items-end justify-between gap-2 sm:col-span-2 sm:justify-end">
                      <span className="text-sm text-ink/70 sm:pb-3" data-testid={`holiday-line-price-${i + 1}`}>
                        {money((SIZES.find((s) => s.value === line.size)?.cents || 0) * (Number(line.qty) || 0))}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeLine(i)}
                        aria-label={`Remove line ${i + 1}`}
                        data-testid={`holiday-remove-line-${i + 1}`}
                        disabled={lines.length === 1}
                        className="mb-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-[#9E2B25]/30 text-[#9E2B25] transition-colors hover:bg-[#9E2B25] hover:text-[#F8F2EA] disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addLine}
                data-testid="holiday-add-line"
                className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#9E2B25]/40 px-5 py-2 text-sm text-[#9E2B25] transition-colors hover:bg-[#9E2B25] hover:text-[#F8F2EA]"
              >
                <Plus size={14} /> Add another item
              </button>

              <div className="mt-6 flex items-center justify-between border-t border-[#E1AA54] pt-5">
                <span className={`font-display text-xl ${RED}`}>Subtotal</span>
                <span className={`font-display text-xl ${RED}`} data-testid="holiday-subtotal">{money(subtotal)}</span>
              </div>

              {/* Fulfillment */}
              <h2 className={`mt-10 font-display text-2xl ${RED}`}>Pickup or delivery?</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {[
                  { value: "pickup", label: "Pickup", hint: "We'll confirm the spot by email" },
                  { value: "delivery", label: "Delivery", hint: "Jersey City & surrounding area" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setFulfillment(opt.value)}
                    data-testid={`holiday-fulfillment-${opt.value}`}
                    aria-pressed={fulfillment === opt.value}
                    className={`cursor-pointer rounded-2xl border p-5 text-left transition-[border-color,background-color] duration-200 ${
                      fulfillment === opt.value ? "border-[#9E2B25] bg-[#9E2B25]/5" : "border-forest/15 hover:border-[#9E2B25]/40"
                    }`}
                  >
                    <span className={`block font-display text-lg ${RED}`}>{opt.label}</span>
                    <span className="mt-1 block text-xs text-ink/60">{opt.hint}</span>
                  </button>
                ))}
              </div>

              {fulfillment === "pickup" && (
                <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="holiday-pickup-fields">
                  <div>
                    <label className="field-label" htmlFor="ho-pickup-date">Pickup date *</label>
                    <input id="ho-pickup-date" type="date" min={today} required value={form.pickup_date} onChange={set("pickup_date")} data-testid="holiday-pickup-date-input" className="field" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="ho-pickup-time">Preferred pickup time</label>
                    <input id="ho-pickup-time" value={form.pickup_time} onChange={set("pickup_time")} data-testid="holiday-pickup-time-input" className="field" placeholder="e.g. 3–5pm" />
                  </div>
                </div>
              )}
              {fulfillment === "delivery" && (
                <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="holiday-delivery-fields">
                  <div>
                    <label className="field-label" htmlFor="ho-delivery-date">Delivery date *</label>
                    <input id="ho-delivery-date" type="date" min={today} required value={form.delivery_date} onChange={set("delivery_date")} data-testid="holiday-delivery-date-input" className="field" />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="ho-address">Delivery address *</label>
                    <input id="ho-address" required value={form.delivery_address} onChange={set("delivery_address")} data-testid="holiday-address-input" className="field" placeholder="Street, apt, Jersey City" />
                    <p className="mt-2 text-xs text-ink/60">Delivery available in Jersey City and the surrounding area.</p>
                  </div>
                </div>
              )}

              {/* Contact */}
              <h2 className={`mt-10 font-display text-2xl ${RED}`}>Your details</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="ho-name">Name *</label>
                  <input id="ho-name" required value={form.name} onChange={set("name")} data-testid="holiday-name-input" className="field" placeholder="Your name" />
                </div>
                <div>
                  <label className="field-label" htmlFor="ho-email">Email *</label>
                  <input id="ho-email" type="email" required value={form.email} onChange={set("email")} data-testid="holiday-email-input" className="field" placeholder="you@email.com" />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="ho-phone">Phone *</label>
                  <input id="ho-phone" type="tel" required value={form.phone} onChange={set("phone")} data-testid="holiday-phone-input" className="field" placeholder="(555) 555-5555" />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="ho-notes">Allergies or notes</label>
                  <textarea id="ho-notes" rows="4" value={form.notes} onChange={set("notes")} data-testid="holiday-notes-textarea" className="field resize-y" placeholder="Tell us about any allergies or restrictions and we will cook around them." />
                </div>
              </div>

              {error && (
                <p data-testid="holiday-error-alert" role="alert" className="mt-5 rounded-lg bg-[#9E2B25]/10 px-4 py-3 text-sm text-[#9E2B25]">
                  {error}
                </p>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-5">
                <button type="submit" disabled={busy || !valid} data-testid="holiday-submit" className={RED_BUTTON}>
                  {busy ? "Placing your order…" : "Place holiday order"}
                </button>
                <span className="text-sm text-ink/70">
                  Subtotal {money(subtotal)} — no card needed now, we'll arrange payment with you.
                </span>
              </div>
            </form>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}